// ✅ src/pages/VehicleV2/LubricationRequestListing.jsx
// UPDATED FULL FILE — production listing design template
//
// Uses backend mount:
//   app.use("/api/LRListing", lubricationRequestListing);
//
// Expected endpoints:
//   GET /api/LRListing/init?branchId=&branchType=&loginUserId=
//   GET /api/LRListing/list?branchId=&month=&year=&search=&page=&pageSize=&loginUserId=
//
// Logic kept:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + listLoading
// - safer init / list flow
// - avoids repeated API calls
// - filter-change page reset without duplicate list firing
// - refresh button
// - pagination / print / details flow
//
// Design updated to match ProductionBOMListing template.

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaPrint,
  FaArrowLeft,
  FaInfoCircle,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/LRListing";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const normalizeBranchOptions = (arr) => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? "Select", "Select"),
    value: String(x.value ?? x.id ?? x.key ?? "Select"),
  }));

  return list.length ? list : [{ label: "Select", value: "Select" }];
};

const LubricationRequestListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  const itemsPerPage = 10;

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || initLoading || listLoading;

  /* ===========================
     ui states
  =========================== */
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  /* ===========================
     dropdown states
  =========================== */
  const [branches, setBranches] = useState([
    { label: "Select", value: "Select" },
  ]);
  const [years, setYears] = useState(["Select"]);
  const [months, setMonths] = useState(["Select"]);

  /* ===========================
     filters
  =========================== */
  const [filters, setFilters] = useState({
    branch: String(sessionBranchId),
    year: "Select",
    month: "Select",
  });

  /* ===========================
     data
  =========================== */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  /* ===========================
     refs
  =========================== */
  const initializedRef = useRef(false);
  const skipNextListRef = useRef(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Number(total || 0) / itemsPerPage)),
    [total]
  );

  /* ===========================
     classes
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  /* ===========================
     render helpers
  =========================== */
  const getBranchLabel = useCallback(
    (row) =>
      row.branchName ||
      row.BranchName ||
      row.branch ||
      row.fld_branchname ||
      "-",
    []
  );

  const getMonth = useCallback(
    (row) => row.month || row.Month || row.fld_month || "-",
    []
  );

  const getYear = useCallback(
    (row) => row.year || row.Year || row.fld_year || "-",
    []
  );

  /* ===========================
     init load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load initial data");
        return;
      }

      const nextBranches = normalizeBranchOptions(data?.branches);
      const nextYears =
        Array.isArray(data?.years) && data.years.length
          ? data.years
          : ["Select"];
      const nextMonths =
        Array.isArray(data?.months) && data.months.length
          ? data.months
          : ["Select"];

      const defaultBranchId = String(
        data?.defaultBranchId ?? sessionBranchId ?? "Select"
      );

      setBranches(nextBranches);
      setYears(nextYears);
      setMonths(nextMonths);

      setFilters((prev) => ({
        ...prev,
        branch: defaultBranchId,
      }));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load dropdowns"
      );
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  /* ===========================
     list load
  =========================== */
  const loadList = useCallback(
    async (page = 1, override = {}) => {
      const branchIdToSend = String(
        override.branch ?? filters.branch ?? "Select"
      );
      const monthToSend = override.month ?? filters.month;
      const yearToSend = override.year ?? filters.year;
      const searchToSend = override.search ?? search;

      if (isSelect(branchIdToSend)) {
        setRows([]);
        setTotal(0);
        return;
      }

      try {
        setListLoading(true);

        const params = {
          branchId: branchIdToSend,
          month: isSelect(monthToSend) ? "Select" : monthToSend,
          year: isSelect(yearToSend) ? "Select" : yearToSend,
          search: toStr(searchToSend),
          page,
          pageSize: itemsPerPage,
          loginUserId,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "List failed");
        }

        setRows(toList(data?.rows));
        setTotal(Number(data?.total || 0));
        setCurrentPage(Number(data?.page || page));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setListLoading(false);
      }
    },
    [filters.branch, filters.month, filters.year, search, loginUserId, commonHeaders]
  );

  /* ===========================
     handlers
  =========================== */
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddNew = useCallback(() => {
    navigate("/vehicle/LubricationRequest");
  }, [navigate]);

  const handleViewDetails = useCallback(
    (row) => {
      const requestId =
        row.fld_lubricationrequestmasid ?? row.requestId ?? row.id;
      if (!requestId) {
        toast.info("RequestId not found in row");
        return;
      }

      navigate("/vehicle/LubricationRequest", {
        state: { requestId, row },
      });
    },
    [navigate]
  );

  const handlePrint = useCallback((row) => {
    const requestId =
      row.fld_lubricationrequestmasid ?? row.requestId ?? row.id;
    if (!requestId) {
      toast.info("RequestId not found in row");
      return;
    }

    const url = `/vehicle/LubricationRequestPrint?requestId=${encodeURIComponent(
      requestId
    )}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    skipNextListRef.current = true;
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    skipNextListRef.current = true;
    setCurrentPage(1);
    setSearch(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    skipNextListRef.current = true;
    setSearch("");
    setCurrentPage(1);
    setFilters({
      branch: String(sessionBranchId),
      year: "Select",
      month: "Select",
    });
    toast.info("Filters cleared!");
  }, [sessionBranchId]);

  const handleSearchClick = useCallback(async () => {
    await loadList(1);
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
    if (!isSelect(filters.branch)) {
      await loadList(1);
    }
  }, [loadInit, loadList, filters.branch]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    if (!filters.branch || filters.branch === "Select") return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      loadList(1, { branch: filters.branch });
    }
  }, [initLoading, filters.branch, loadList]);

  useEffect(() => {
    if (initLoading) return;
    if (!initializedRef.current) return;
    if (!filters.branch || filters.branch === "Select") return;

    if (skipNextListRef.current) {
      skipNextListRef.current = false;
      return;
    }

    loadList(currentPage);
  }, [
    currentPage,
    filters.branch,
    filters.month,
    filters.year,
    search,
    initLoading,
    loadList,
  ]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">LUBRICATION REQUEST</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Lubrication Request Listing
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {sessionBranchId || "-"} • User ID:{" "}
                {loginUserId || "-"} • Records: {total}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearchClick}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={branchType !== "HO" || isBusy}
                  >
                    {branches.map((b, idx) => (
                      <option key={`${b.value}-${idx}`} value={String(b.value)}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {years.map((y, idx) => (
                      <option key={`${y}-${idx}`} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select
                    name="month"
                    value={filters.month}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {months.map((m, idx) => (
                      <option key={`${m}-${idx}`} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Search</label>
                  <input
                    value={search}
                    onChange={handleSearchInputChange}
                    placeholder="Search by branch / month / year..."
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || initLoading || listLoading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading
                    ? "Loading page..."
                    : initLoading
                    ? "Loading initial data..."
                    : "Loading..."}
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      S.No
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Branch
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Month
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Year
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      Details
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      Print
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  } divide-y`}
                >
                  {rows.map((row, idx) => (
                    <tr
                      key={row.requestId ?? row.id ?? idx}
                      className={`${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                      }`}
                    >
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {getBranchLabel(row)}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getMonth(row)}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {getYear(row)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewDetails(row)}
                          type="button"
                          className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                          title="Details"
                        >
                          <FaInfoCircle className="inline w-4 h-4" />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handlePrint(row)}
                          type="button"
                          className={`text-lg transition ${
                            isDark
                              ? "text-gray-300 hover:text-white"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                          title="Print"
                        >
                          <FaPrint className="inline w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {totalPages > 1 && !isBusy && rows.length > 0 && (
            <div className="mt-6 flex justify-center">
              <nav className="flex gap-1 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === p
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubricationRequestListing;